import { ECountriesFolderType, EFolderType, ESnapshotsFolderType, IFolder } from '../types/folders.types';
import {
  ArrowDownloadRegular,
  CopyRegular,
  DeleteRegular,
  DocumentArrowDownRegular,
  DocumentMultipleSyncRegular,
  DocumentTableRegular,
  FolderArrowRightRegular,
  LockClosedRegular,
  LockOpenRegular,
  PeopleRegular,
  RenameRegular,
  TextBulletListTreeRegular,
} from '@fluentui/react-icons';
import { useCallback, useContext, useMemo } from 'react';

import getReferenceItemLabel from '@modules/documents/helpers/getReferenceItemLabel.helper';

import {
  TItem,
  TItemType,
  useArchive,
  useCopy,
  useGetDependants,
  useGetVersionInfo,
  useMove,
  useRename,
  TSelectFolderDialogState,
  TExportDialogState,
  splitExtension,
  TSelectVersionDialogState,
  TSelectItemDialogState,
  TVersionPreferenceDialogState,
  useUpdateFrozen,
  useGenerateIndex,
  useCloneStructure,
} from '@modules/items';
import { AccessType, TAccessDialogState, getCanEdit } from '@modules/access';
import { useCreate as useCreateReplica } from '@modules/replicas';
import { useBulkExport } from '../api/bulkExports.api';
import { TDraft, TVersion } from '@modules/documents';
import { TFileVersion } from '@modules/files';
import { USER_GROUPS, SignInContext } from '@modules/users';

import { ToastContext } from '@shared/contexts';
import { TSingleFieldDialogState, TConfirmDialogState, TDisplayItem, CustomMessageBar } from '@shared/components';

import { TAction, TCopyVersionPreference, TVersionPreference } from '../types/items.types';
import { TERMINOLOGY } from '@shared/i18n';

const LATEST_VERSION_PREFERENCE: TVersionPreference = 'VERSION';
const PULL_VERSION_PREFERENCE: TCopyVersionPreference = 'APPROVED_AND_LATEST';

type OptionalShowAction = Omit<TAction, 'show'> & { show?: TAction['show'] };
const appendShowMethod = (actions: OptionalShowAction[]): TAction[] =>
  actions.map((action) => ({
    ...action,
    show: action.show || (() => true), // default to always show if not defined
  }));

type TSynchronizableItem = TItem & { type: 'DOCUMENT' | 'FILE' };
type TCreateReplicaInput = {
  _id: string;
  itemType: TSynchronizableItem['type'];
  versionMode: 'AUTO' | 'SPECIFIC';
  versionId?: string;
};

const isSynchronizableItem = (item: TItem): item is TSynchronizableItem =>
  item.type === 'DOCUMENT' || item.type === 'FILE';
const isSynchronizableSelection = (items: TItem[]): items is TSynchronizableItem[] =>
  items.length > 0 && items.every(isSynchronizableItem);

type TGeneralActionsParams = {
  setSimpleDialogState: (state: TSingleFieldDialogState<'input'>) => void;
  setConfirmDialogState: (state: TConfirmDialogState) => void;
  setAccessDialogState: (state: TAccessDialogState) => void;
  // optional params
  folder?: IFolder;
  isBlockFolderType?: boolean;
};
export const useGeneralActions = ({
  setSimpleDialogState,
  setConfirmDialogState,
  setAccessDialogState,
  folder,
  isBlockFolderType = false,
}: TGeneralActionsParams): TAction[] => {
  // --- ACCESS:
  const handleAccess = useCallback(
    (affectedItems: TItem[]) => {
      if (affectedItems.length > 1) return;
      const isItem = affectedItems.length === 1 || !folder;

      setAccessDialogState({
        isOpen: true,
        id: isItem ? affectedItems[0]._id : folder._id,
        nodeType: isItem ? affectedItems[0].type : 'FOLDER',
        name: isItem ? affectedItems[0].name : folder.name,
      });
    },
    [folder, setAccessDialogState]
  );

  // --- ARCHIVE:
  const archive = useArchive();
  const getDependants = useGetDependants();
  const handleArchive = useCallback(
    async (affectedItems: TItem[]) => {
      if (affectedItems.length !== 1) return;
      const item = affectedItems[0];

      const { inRefs, taggedSnippetCount, replicas } = await getDependants.mutateAsync({
        id: item._id,
        itemType: item.type,
      });

      const displayItems = inRefs.map(
        (ref): TDisplayItem => ({
          id: ref.id,
          duplicateId: ref.sourceDocId,
          text: getReferenceItemLabel(ref.sourceDocName),
          href: `/document/${ref.sourceDocId}?view=latest&elem=${ref.id}`,
        })
      );

      const replicaDisplayItems = replicas.map(
        (r): TDisplayItem => ({
          id: r._id,
          text: r.name,
          href: `/synced/${r._id}`,
        })
      );

      const hasWarnings = displayItems.length > 0 || taggedSnippetCount > 0 || replicaDisplayItems.length > 0;

      const dialogState: TConfirmDialogState = {
        isOpen: true,
        title: `Archive ${item.name}?`,
        content: hasWarnings ? (
          <div className="flex flex-col gap-2">
            {displayItems.length > 0 && (
              <CustomMessageBar
                intent="warning"
                multi
                body={`Archiving will cause errors for ${displayItems.length} ${displayItems.length === 1 ? TERMINOLOGY.reference.lower : TERMINOLOGY.reference.lowerPlural} in the following documents:`}
                items={displayItems}
              />
            )}
            {taggedSnippetCount > 0 && (
              <CustomMessageBar
                intent="warning"
                multi
                body={`This ${TERMINOLOGY.inlineBlock.lower} is selected as a tagged ${TERMINOLOGY.inlineBlock.lower} in ${taggedSnippetCount} document${taggedSnippetCount === 1 ? '' : 's'} (across all products).`}
              />
            )}
            {replicaDisplayItems.length > 0 && (
              <CustomMessageBar
                intent="warning"
                multi
                body={`${item.type === 'FOLDER' ? 'Items in this folder have' : 'This item has'} ${replicaDisplayItems.length} ${replicaDisplayItems.length === 1 ? TERMINOLOGY.replica.lower : TERMINOLOGY.replica.lowerPlural}:`}
                items={replicaDisplayItems}
              />
            )}
          </div>
        ) : (
          'Are you sure you want to archive this item?'
        ),
        buttonLabel: 'Archive',
        onConfirm: async () => {
          // async to move button to loading state
          await archive.mutateAsync(
            {
              isProduct: !folder,
              parent: folder,
              sourceId: item._id,
              itemType: item.type,
            },
            { onSuccess: () => setConfirmDialogState({ ...dialogState, isOpen: false }) }
          );
        },
      };
      setConfirmDialogState(dialogState);
    },
    [archive, folder?._id, setConfirmDialogState]
  );

  // --- RENAME:
  const rename = useRename();
  const handleRename = useCallback(
    (affectedItems: TItem[]) => {
      let parent: IFolder | undefined = undefined;
      let id: string;
      let itemType: TItemType;
      let defaultText: string;
      let afterLabel = '';
      if (affectedItems.length === 1) {
        parent = folder;
        id = affectedItems[0]._id;
        itemType = affectedItems[0].type;
        defaultText = affectedItems[0].name;
        if (
          !isBlockFolderType &&
          ['FILE', 'DOCUMENT', 'REPLICA_FILE', 'REPLICA_DOCUMENT'].includes(affectedItems[0].type)
        ) {
          const res = splitExtension(affectedItems[0].name);
          if (res[1]) {
            defaultText = res[0];
            afterLabel = `.${res[1]}`;
          }
        }
      } else if (folder) {
        id = folder._id;
        itemType = 'FOLDER';
        defaultText = folder.name;
      } else return console.error('No folder or item provided for renaming!');

      const dialogState: TSingleFieldDialogState<'input'> = {
        isOpen: true,
        defaultText,
        title: 'Rename',
        label: 'Name',
        buttonLabel: 'Rename',
        afterLabel,
        onSubmit: (data) => {
          rename.mutate(
            { isProduct: !folder, parent, id, itemType, name: data.text + afterLabel },
            { onSuccess: () => setSimpleDialogState({ ...dialogState, isOpen: false }) }
          );
        },
      };
      setSimpleDialogState(dialogState);
    },
    [folder, rename, setSimpleDialogState]
  );

  const { user } = useContext(SignInContext);

  return useMemo(() => {
    const actions: OptionalShowAction[] = [
      {
        children: 'Access',
        icon: <PeopleRegular />,
        order: 1,
        enable: (affectedItems) =>
          (affectedItems.length === 0 && folder && folder.accessRight >= AccessType.MANAGE) ||
          (affectedItems.length === 1 && affectedItems[0].accessRight >= AccessType.MANAGE),
        onClick: handleAccess,
      },
      {
        children: 'Archive',
        icon: <DeleteRegular />,
        order: 4,
        enable: (affectedItems) =>
          affectedItems.length === 1 &&
          (folder ? folder.accessRight >= AccessType.EDIT : user.groups.includes(USER_GROUPS.ADMIN)),
        onClick: handleArchive,
      },
      {
        children: 'Rename',
        icon: <RenameRegular />,
        order: 5,
        enable: (affectedItems) =>
          // (affectedItems.length === 0 && folder.accessRight >= AccessType.EDIT) ||
          affectedItems.length === 1 && getCanEdit(user, affectedItems[0].accessRight, { authorGroup: false }).canEdit,
        onClick: handleRename,
      },
    ];

    return appendShowMethod(actions);
  }, [folder, user, handleAccess, handleArchive, handleRename]);
};

const useActions = (
  setSimpleDialogState: (state: TSingleFieldDialogState<'input'>) => void,
  setMoveDialogState: (state: TSelectFolderDialogState) => void,
  setReplicaDialogState: (state: TSelectFolderDialogState) => void,
  setSelectVersionDialogState: (state?: TSelectVersionDialogState) => void,
  setConfirmDialogState: (state: TConfirmDialogState) => void,
  setAccessDialogState: (state: TAccessDialogState) => void,
  setExportDialogState: (state: TExportDialogState) => void,
  setVersionPreferenceDialogState: (state: TVersionPreferenceDialogState) => void,
  setPullDialogState: (state: TSelectItemDialogState) => void,
  setCloneStructureDialogState: (state: TSelectItemDialogState) => void,
  folder: IFolder,
  isBlockFolderType: boolean,
  countriesFolderType: ECountriesFolderType | undefined,
  snapshotsFolderType: ESnapshotsFolderType | undefined
): TAction[] => {
  const { show, showProgress } = useContext(ToastContext);
  const { user } = useContext(SignInContext);
  const isCountryFolderType = folder.folderType === EFolderType.COUNTRY;
  const isSnapshotFolderType = folder.folderType === EFolderType.SNAPSHOT;
  const shouldUseLatestVersionPreference = isCountryFolderType || isSnapshotFolderType;
  const isWithinSingleCountryRegistration = isCountryFolderType && !countriesFolderType;
  const isWithinSingleSnapshot = isSnapshotFolderType && !snapshotsFolderType;

  // --- MOVE:
  const move = useMove();
  const handleMove = useCallback(
    (affectedItems: TItem[]) => {
      const dialogState: TSelectFolderDialogState = {
        isOpen: true,
        affectedItems,
        select: async (targetId) => {
          if (folder._id === targetId) return setMoveDialogState({ ...dialogState, isOpen: false });

          await move.mutateAsync(
            {
              parent: folder,
              items: affectedItems.map((item) => ({ _id: item._id, itemType: item.type })),
              targetId,
            },
            { onSuccess: () => setMoveDialogState({ ...dialogState, isOpen: false }) }
          );
        },
      };
      setMoveDialogState(dialogState);
    },
    [folder, move, setMoveDialogState]
  );

  // --- COPY:
  const copy = useCopy();
  const getVersionInfo = useGetVersionInfo();
  const handleCopy = useCallback(
    async (affectedItems: TItem[]) => {
      if (affectedItems.length !== 1) return;
      const hideProgress = showProgress('Copying...');
      try {
        await copy.mutateAsync({
          destinationFolderId: folder._id,
          items: [{ _id: affectedItems[0]._id, itemType: affectedItems[0].type }],
        });
      } finally {
        hideProgress();
      }
    },
    [copy, folder._id, showProgress]
  );

  // --- PULL:
  const pullItems = useCallback(
    async (
      affectedItems: TItem[],
      selectedItems: TItem[],
      {
        versionPreference,
        versionId,
      }: {
        versionPreference?: TCopyVersionPreference;
        versionId?: string;
      } = {}
    ) => {
      const isItem = affectedItems.length === 1;
      const hideProgress = showProgress('Pulling...');
      try {
        await copy.mutateAsync({
          destinationFolderId: isItem ? affectedItems[0]._id : folder._id, // folder into which items will be pulled
          items: selectedItems.map((i) => ({ _id: i._id, itemType: i.type })),
          versionPreference,
          versionId,
          pull: true,
        });
      } finally {
        hideProgress();
      }
    },
    [copy, folder._id, showProgress]
  );

  const handlePull = useCallback(
    async (affectedItems: TItem[]) => {
      if (!folder || affectedItems.length > 1 || !setPullDialogState) return;

      const dialogState: TSelectItemDialogState = {
        isOpen: true,

        onSelect: async (selectedItems) => {
          setPullDialogState({ ...dialogState, isOpen: false });

          if (selectedItems.length === 1 && selectedItems[0].type !== 'FOLDER') {
            // single item - select version dialog:

            const selectedItem = selectedItems[0];
            const versionInfo = await getVersionInfo.mutateAsync({ id: selectedItem._id, itemType: selectedItem.type });
            if (!versionInfo.versions.length)
              return show('error', `There is no complete version for ${selectedItem.name}!`);
            if (folder.folderType === EFolderType.SNAPSHOT) delete versionInfo.draft;

            const dialogState2: TSelectVersionDialogState = {
              title: 'Select version to pull',
              isOpen: true,
              versionInfo,
              optionTexts: {
                AUTO: {
                  subtitle: 'Approved / latest',
                  tooltip:
                    'Pulls the latest approved version. If no approved version is available, it uses the latest version instead.',
                },
                SPECIFIC: {
                  tooltip: 'Select and pull a specific version.',
                },
              },
              onSelect: async (versionMode, versionId) => {
                setSelectVersionDialogState({ ...dialogState2, isOpen: false });

                await pullItems(affectedItems, selectedItems, {
                  versionPreference: versionMode === 'AUTO' ? PULL_VERSION_PREFERENCE : undefined,
                  versionId: versionMode === 'SPECIFIC' ? versionId : undefined,
                });
              },
            };
            setSelectVersionDialogState(dialogState2);
          } else {
            // multiple items or single folder - use pull-specific automatic preference:
            await pullItems(affectedItems, selectedItems, { versionPreference: PULL_VERSION_PREFERENCE });
          }
        },
      };
      setPullDialogState(dialogState);
    },
    [folder, getVersionInfo, pullItems, setPullDialogState, setSelectVersionDialogState, show]
  );

  // --- CLONE STRUCTURE:
  const cloneStructure = useCloneStructure();
  const handleCloneStructure = useCallback(() => {
    const dialogState: TSelectItemDialogState = {
      isOpen: true,
      onSelect: async ([sourceFolder]) => {
        setCloneStructureDialogState({ ...dialogState, isOpen: false });
        const hideProgress = showProgress('Cloning structure...');
        try {
          await cloneStructure.mutateAsync({ targetFolderId: folder._id, sourceFolderId: sourceFolder._id });
        } finally {
          hideProgress();
        }
      },
    };
    setCloneStructureDialogState(dialogState);
  }, [folder._id, setCloneStructureDialogState, showProgress]);

  // --- FREEZE / UNFREEZE:
  const updateFrozen = useUpdateFrozen();
  const handleUpdateFrozen = useCallback(
    async (affectedItems: TItem[]) => {
      if (affectedItems.length > 0) return;
      await updateFrozen.mutateAsync({
        id: folder._id,
        folderType: folder.folderType,
        isFrozen: !folder.isFrozen,
      });
    },
    [folder._id, folder.folderType, folder.isFrozen, updateFrozen]
  );

  // --- EXPORT:
  const bulkExport = useBulkExport();
  const handleExport = useCallback(
    async (affectedItems: TItem[]) => {
      const itemsToExport = affectedItems.length >= 1 ? affectedItems : [folder];

      if (shouldUseLatestVersionPreference) {
        await bulkExport.mutateAsync({
          itemIds: itemsToExport.map((item) => item._id),
          mode: LATEST_VERSION_PREFERENCE,
        });
        return;
      }

      setExportDialogState({
        isOpen: true,
        affectedItems: itemsToExport,
      });
    },
    [bulkExport, folder, setExportDialogState, shouldUseLatestVersionPreference]
  );

  // --- CREATE REPLICA:
  const createReplica = useCreateReplica();
  const openReplicaTargetDialog = useCallback(
    (affectedItems: TSynchronizableItem[], items: TCreateReplicaInput[]) => {
      const dialogState: TSelectFolderDialogState = {
        isOpen: true,
        affectedItems,
        select: async (targetId) => {
          await createReplica.mutateAsync(
            {
              parent: targetId,
              items,
            },
            { onSuccess: () => setReplicaDialogState({ ...dialogState, isOpen: false }) }
          );
        },
      };
      setReplicaDialogState(dialogState);
    },
    [createReplica, setReplicaDialogState]
  );

  const handleCreateReplica = useCallback(
    async (affectedItems: TItem[]) => {
      if (!isSynchronizableSelection(affectedItems)) return;

      if (affectedItems.length > 1) {
        openReplicaTargetDialog(
          affectedItems,
          affectedItems.map((item) => ({
            _id: item._id,
            itemType: item.type,
            versionMode: 'AUTO',
          }))
        );
        return;
      }

      const affectedItem = affectedItems[0];
      const versionInfo = await getVersionInfo.mutateAsync({ id: affectedItem._id, itemType: affectedItem.type });
      if (!versionInfo.draft && !versionInfo.versions.length)
        return show('error', `${affectedItem.name} has no draft or version to ${TERMINOLOGY.synchronize.lower}.`);

      setSelectVersionDialogState({
        title: `Select version to ${TERMINOLOGY.synchronize.lower}`,
        isOpen: true,
        versionInfo,
        onSelect: async (versionMode, versionId) => {
          let version: TVersion | TFileVersion | TDraft | undefined;
          if (versionMode === 'AUTO') version = versionInfo.draft ? versionInfo.draft : versionInfo.versions[0];
          else
            version = versionId === 'draft' ? versionInfo.draft : versionInfo.versions.find((v) => v.id === versionId);
          if (!version) {
            console.error(`Version not found for synchronizing! (${versionMode}, ${versionId})`);
            return show('error', 'Action failed!');
          }

          openReplicaTargetDialog(affectedItems, [
            {
              _id: affectedItem._id,
              itemType: affectedItem.type,
              versionMode,
              versionId,
            },
          ]);
          setSelectVersionDialogState(undefined);
        },
      });
    },
    [getVersionInfo, openReplicaTargetDialog, setSelectVersionDialogState, show]
  );

  // --- GENERATE INDEX:
  const generateIndex = useGenerateIndex();
  const runGenerateIndex = useCallback(
    async (versionPreference: TVersionPreference) => {
      const hideProgress = showProgress('Generating index...');
      try {
        await generateIndex.mutateAsync({ folderId: folder._id, mode: versionPreference });
      } finally {
        hideProgress();
      }
    },
    [folder._id, generateIndex, showProgress]
  );

  const handleGenerateIndex = useCallback(async () => {
    if (shouldUseLatestVersionPreference) {
      await runGenerateIndex(LATEST_VERSION_PREFERENCE);
      return;
    }

    const dialogState: TVersionPreferenceDialogState = {
      isOpen: true,
      buttonLabel: 'Generate index',
      buttonIcon: <DocumentTableRegular />,
      onSelect: async (versionPreference) => {
        setVersionPreferenceDialogState({ ...dialogState, isOpen: false });

        await runGenerateIndex(versionPreference);
      },
    };
    setVersionPreferenceDialogState(dialogState);
  }, [runGenerateIndex, setVersionPreferenceDialogState, shouldUseLatestVersionPreference]);

  // --- ACTIONS:
  const generalActions = useGeneralActions({
    setSimpleDialogState,
    setConfirmDialogState,
    setAccessDialogState,
    folder,
    isBlockFolderType,
  });
  return useMemo(() => {
    const actions: OptionalShowAction[] = [
      ...generalActions,
      {
        children: 'Pull',
        icon: <DocumentArrowDownRegular />,
        order: 0,
        // show only for the countries module:
        show: () =>
          (folder.folderType === EFolderType.COUNTRY && !countriesFolderType) ||
          (folder.folderType === EFolderType.SNAPSHOT && !snapshotsFolderType), // WE CANNOT PULL INTO A SINGLE COUNTRY REGISTRATION FROM THE COUNTRY VIEW YET, BECAUSE THE DIALOG WOULD OPEN IN THE WRONG FOLDER
        enable: (affectedItems) =>
          (affectedItems.length === 0 && folder.accessRight >= AccessType.EDIT) ||
          (affectedItems.length === 1 &&
            affectedItems[0].accessRight >= AccessType.EDIT &&
            affectedItems[0].type === 'FOLDER'),
        onClick: handlePull,
      },
      {
        children: 'Clone structure',
        icon: <TextBulletListTreeRegular />,
        order: 0,
        // toolbar only: the row menus pass the row as an affected item
        show: (affectedItems) => affectedItems.length === 0 && isBlockFolderType,
        enable: () => user.groups.includes(USER_GROUPS.AUTHOR) && folder.accessRight >= AccessType.EDIT,
        onClick: handleCloneStructure,
      },
      {
        children: folder.isFrozen ? TERMINOLOGY.unfreeze.upper : TERMINOLOGY.freeze.upper,
        icon: folder.isFrozen ? <LockOpenRegular /> : <LockClosedRegular />,
        order: 0,
        // show only for a country registration or snapshot folder:
        show: (affectedItems) =>
          affectedItems.length === 0 &&
          ((folder.folderType === EFolderType.COUNTRY && folder.resolvedPath.length === 2) ||
            (folder.folderType === EFolderType.SNAPSHOT && folder.resolvedPath.length === 3)),
        enable: (affectedItems) => affectedItems.length === 0 && folder.accessRightAllowFrozen >= AccessType.EDIT,
        onClick: handleUpdateFrozen,
      },
      {
        children: 'Move to',
        icon: <FolderArrowRightRegular />,
        order: 2,
        enable: (affectedItems) =>
          affectedItems.length >= 1 &&
          folder.accessRight >= AccessType.EDIT &&
          !countriesFolderType && // is undefined within a single country registration (or outside the countries space)
          !snapshotsFolderType, // is undefined within a single snapshot (or outside the snapshots space)
        onClick: handleMove,
      },
      {
        children: 'Copy',
        icon: <CopyRegular />,
        order: 3,
        enable: (affectedItems) =>
          affectedItems.length === 1 &&
          folder.accessRight >= AccessType.EDIT &&
          // Replicas cannot be copied - use "Synchronize" from the master to create new replicas.
          !['REPLICA_FILE', 'REPLICA_DOCUMENT'].includes(affectedItems[0].type) &&
          countriesFolderType !== ECountriesFolderType.ROOT,
        onClick: handleCopy,
      },
      /*
      {
        children: 'Approve',
        icon: <CheckboxPersonRegular />,
        order: 5,
        enable: (affectedItems) => (
          affectedItems.length >= 1 && affectedItems.every(item => item.accessRight >= AccessType.EDIT) &&
          affectedItems.every(item => item.stats.latestVersionId !== item.stats.approvedVersionId)
        ),
        getDisabledMessage: (affectedItems) => 'Disabled in demo version.',
        onClick: () => console.log('Approve')
      },
      */
      {
        children: TERMINOLOGY.synchronize.upper,
        icon: <DocumentMultipleSyncRegular />,
        order: 6,
        show: (affectedItems) => !isBlockFolderType && !countriesFolderType && isSynchronizableSelection(affectedItems),
        enable: (affectedItems) =>
          isSynchronizableSelection(affectedItems) &&
          affectedItems.every((item) => item.accessRight >= AccessType.EDIT),
        onClick: handleCreateReplica,
      },
      {
        children: 'Export',
        icon: <ArrowDownloadRegular />,
        order: 7,
        enable: (affectedItems) =>
          !isBlockFolderType &&
          // registration-country module specific conditions:
          (countriesFolderType
            ? // do not show for the root registrations folder
              countriesFolderType !== ECountriesFolderType.ROOT &&
              // show only for the sub-items of the registration-country folder
              (countriesFolderType === ECountriesFolderType.COUNTRY ? affectedItems.length > 0 : false)
            : true),
        onClick: handleExport,
      },
      {
        children: 'Generate index',
        icon: <DocumentTableRegular />,
        order: 8,
        show: (affectedItems) =>
          affectedItems.length === 0 && (isWithinSingleCountryRegistration || isWithinSingleSnapshot),
        enable: (affectedItems) =>
          affectedItems.length === 0 &&
          user.groups.includes(USER_GROUPS.AUTHOR) &&
          folder.accessRight >= AccessType.EDIT,
        onClick: handleGenerateIndex,
      },
    ];
    return appendShowMethod(actions).sort((a, b) => a.order - b.order);
  }, [
    countriesFolderType,
    folder,
    generalActions,
    isWithinSingleCountryRegistration,
    isWithinSingleSnapshot,
    handleCloneStructure,
    handleCopy,
    handleCreateReplica,
    handleExport,
    handleGenerateIndex,
    handleMove,
    handlePull,
    handleUpdateFrozen,
    isBlockFolderType,
    snapshotsFolderType,
    user.groups,
  ]);
};

export default useActions;

/*
      disabledMessage: (
        selectedItems.every(item => item.accessRight >= AccessType.EDIT) ? (
          selectedItems.every(item => (item.stats.latestVersionId && (item.stats.latestVersionId !== item.stats.approvedVersionId))) ?
          undefined : 'No unapproved version available for a selected item.'
        ) : 'Access rights missing to approve the selected items.'
      ),
  */

/*
    Access (single / Manage)
    Rename (single / Edit)
    Copy (multi / Edit)
    Move to (multi / Edit)
    Export (multi)
    Archive (multi / Edit)
    Approve (multi / Edit)
  */
